/**
 * shadcn Popover + Button 기반 TimePicker.
 * DatePicker와 동일한 트리거 스타일, 값은 HH:mm 문자열.
 */
import { Button } from '@/shared/components/ui/button';
import { ClearFieldButton } from '@/shared/components/ui/clear-field-button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import {
  buildHourOptions,
  buildMinuteOptions,
  composeTimeValue,
  formatTimeDisplay,
  parseTimeValue,
} from '@/shared/lib/timePickerUtils';
import { cn } from '@/shared/lib/utils';
import { Clock } from 'lucide-react';
import * as React from 'react';

const triggerClassName = cn(
  'h-9 w-full justify-start gap-2 px-3 text-left text-sm font-normal',
  'border border-ruda-border-light bg-white shadow-none',
  'hover:border-ruda-border-dark hover:bg-ruda-grey-50',
);

export interface TimePickerProps {
  id?: string;
  /** HH:mm */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** true면 팝오버를 열지 않고 표시만 */
  readOnly?: boolean;
  className?: string;
  /** HH:mm inclusive */
  min?: string;
  /** HH:mm inclusive */
  max?: string;
  /** 분 간격 (기본 1) */
  minuteStep?: number;
  /**
   * 중첩 Popover 안에서 쓸 때 false.
   * Radix 기본 modal=true면 바깥 패널이 함께 닫힐 수 있다.
   */
  modal?: boolean;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  id,
  value,
  onChange,
  placeholder = '시간 선택',
  disabled,
  readOnly,
  className,
  min,
  max,
  minuteStep = 1,
  modal = true,
}) => {
  const [open, setOpen] = React.useState(false);
  const parsed = parseTimeValue(value);
  const [valueHour, valueMinute] = (parsed ?? '09:00').split(':').map(Number);
  // 팝오버 열린 동안 controlled value와 무관하게 시·분 초안을 유지
  const [draftHour, setDraftHour] = React.useState(valueHour);
  const [draftMinute, setDraftMinute] = React.useState(valueMinute);

  React.useEffect(() => {
    if (!open) {
      setDraftHour(valueHour);
      setDraftMinute(valueMinute);
    }
  }, [open, valueHour, valueMinute]);

  const hours = React.useMemo(() => buildHourOptions(min, max), [min, max]);
  const minutes = React.useMemo(
    () => buildMinuteOptions(draftHour, min, max, minuteStep),
    [draftHour, min, max, minuteStep],
  );

  const display = formatTimeDisplay(value);
  const isDisabled = Boolean(disabled || readOnly);
  const canClear = Boolean(display) && !isDisabled;

  const selectHour = (nextHour: number) => {
    const allowedMinutes = buildMinuteOptions(nextHour, min, max, minuteStep);
    const nextMinute = allowedMinutes.includes(draftMinute)
      ? draftMinute
      : (allowedMinutes[0] ?? 0);
    setDraftHour(nextHour);
    setDraftMinute(nextMinute);
  };

  const selectMinute = (nextMinute: number) => {
    setDraftMinute(nextMinute);
    onChange?.(composeTimeValue(draftHour, nextMinute));
    setOpen(false);
  };

  return (
    <div className={cn('relative w-full', className)}>
      <Popover
        modal={modal}
        open={readOnly ? false : open}
        onOpenChange={(next) => {
          if (isDisabled && !readOnly) return;
          if (readOnly) return;
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              triggerClassName,
              !display && 'text-muted-foreground',
              readOnly && 'cursor-default opacity-100',
              canClear && 'pr-8',
            )}
          >
            <Clock className="size-4 shrink-0 opacity-70" />
            {display || placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <div className="flex gap-3">
            <div className="flex flex-col gap-1">
              <span className="px-1 text-xs font-medium text-muted-foreground">
                시
              </span>
              <div className="h-48 w-14 overflow-y-auto rounded-12 border border-border p-1">
                {hours.map((h) => (
                  <Button
                    key={h}
                    type="button"
                    size="sm"
                    variant={h === draftHour ? 'default' : 'ghost'}
                    className="h-8 w-full justify-center px-0"
                    aria-label={`시 ${String(h).padStart(2, '0')}`}
                    onClick={() => selectHour(h)}
                  >
                    {String(h).padStart(2, '0')}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="px-1 text-xs font-medium text-muted-foreground">
                분
              </span>
              <div className="h-48 w-14 overflow-y-auto rounded-12 border border-border p-1">
                {minutes.map((m) => (
                  <Button
                    key={m}
                    type="button"
                    size="sm"
                    variant={m === draftMinute ? 'default' : 'ghost'}
                    className="h-8 w-full justify-center px-0"
                    aria-label={`분 ${String(m).padStart(2, '0')}`}
                    onClick={() => selectMinute(m)}
                  >
                    {String(m).padStart(2, '0')}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {canClear ? (
        <ClearFieldButton onClick={() => onChange?.('')} />
      ) : null}
    </div>
  );
};
